import { useState, useCallback, useMemo, useEffect } from 'react';
import { ExportColumnsDialog } from './ExportColumnsDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { 
  Truck, Plus, Edit, Trash2, Copy, Check, User, Loader2, Pencil,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Paperclip, Download,
  Columns3
} from 'lucide-react';
import { CUSTOMERS } from '../utils/constants';
import { formatCurrency, formatHours } from '../utils/calculations';
import { Money } from '../../../components/Money';

// Editable cell wrapper component
function EditableCell({ children, isEditable = true, onDoubleClick, className = "" }) {
  if (!isEditable) {
    return <span className={`text-slate-400 ${className}`}>{children}</span>;
  }
  
  return (
    <div 
      className={`group/cell relative cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1 -mx-1 ${className}`}
      onDoubleClick={onDoubleClick}
    >
      <span className="group-hover/cell:underline decoration-dotted">{children}</span>
      <Pencil className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
    </div>
  );
}

export function RoutesTable({
  routes,
  loading,
  selectedCustomers,
  isMultiCustomer,
  selectedRows,
  bulkDeleting,
  bulkCloning,
  recentlyClonedIds,
  firms = [],
  customerList = [],
  onToggleRow,
  onToggleAllRows,
  onBulkDelete,
  onBulkClone,
  onEdit,
  onDelete,
  onClone,
  onAdd,
  onUpdateInline,
  onUpdateDriver,
  onFetchEmployees,
  onViewAttachments,
  t
}) {
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [inlineDriverOpen, setInlineDriverOpen] = useState(null);
  const [inlineCopilotOpen, setInlineCopilotOpen] = useState(null);
  const [inlineEmployees, setInlineEmployees] = useState([]);

  // Resolve customer name from static CUSTOMERS or dynamic customerList
  const getCustomerName = useCallback((customerId) => {
    if (!customerId) return '';
    const staticMatch = CUSTOMERS.find(c => c.id === customerId);
    if (staticMatch) return staticMatch.name;
    const dynamicMatch = customerList.find(c => c.slug === customerId || c.id === customerId);
    if (dynamicMatch) return dynamicMatch.name;
    return customerId;
  }, [customerList]);

  const getCalcType = useCallback((customerId) => {
    if (!customerId) return 'hours_rate';
    const dynamicMatch = customerList.find(c => c.slug === customerId || c.id === customerId);
    if (dynamicMatch && dynamicMatch.calculation_type) return dynamicMatch.calculation_type;
    // Legacy fallback
    if (customerId === 'bpost') return 'manual_total';
    if (customerId === 'dhl') return 'stops_price';
    return 'hours_rate';
  }, [customerList]);

  const getCustomerColor = useCallback((customerId) => {
    if (!customerId) return 'bg-gray-100 text-gray-700';
    const staticMatch = CUSTOMERS.find(c => c.id === customerId);
    if (staticMatch) return staticMatch.color;
    const dynamicMatch = customerList.find(c => c.slug === customerId || c.id === customerId);
    if (dynamicMatch && dynamicMatch.color) return dynamicMatch.color;
    return 'bg-slate-100 text-slate-700';
  }, [customerList]);

  // Derive the correct identifier field + label per customer. A customer's
  // `fields` array drives what the edit form asks for; the table must mirror
  // that or users see stale "Tur" labels with empty values for customers like
  // Mainfreight that actually use `route_number`.
  const getRouteIdentifier = useCallback((customerId) => {
    const dyn = customerList.find(c => c.slug === customerId || c.id === customerId);
    const fields = dyn?.fields || [];
    // route_number takes precedence (fedex, dhl, bpost, mainfreight, …)
    if (fields.includes('route_number') || ['fedex', 'dhl', 'bpost'].includes(customerId)) {
      return { key: 'route_number', label: t('routes.routeNumber') || 'Route Nr' };
    }
    if (fields.includes('rit') || customerId === 'mover') {
      return { key: 'plaats', label: t('routes.rit') || 'Rit' };
    }
    return { key: 'route_type', label: t('routes.tour') || 'Tur' };
  }, [customerList, t]);

  // Helper: does ANY currently-selected customer have a given field in its
  // backend-defined `fields` array? Used so column visibility for copilot /
  // km / stops is data-driven instead of hardcoded per customer id. Falls
  // back to a legacy customer-id list for the static CUSTOMERS without a
  // dynamic record yet.
  const anySelectedHasField = useCallback((field, legacyIds = []) => {
    for (const cid of selectedCustomers) {
      const dyn = customerList.find(c => c.slug === cid || c.id === cid);
      const fields = dyn?.fields || [];
      if (fields.includes(field)) return true;
      if (legacyIds.includes(cid)) return true;
    }
    return false;
  }, [selectedCustomers, customerList]);

  // Helper: does a specific route's customer define a given field?
  // Used to decide whether to show a VALUE or a "-" placeholder inside a
  // per-row cell. Data-driven — relies on customer.fields.
  const routeHasField = useCallback((route, field) => {
    const cid = route?.customer;
    const dyn = customerList.find(c => c.slug === cid || c.id === cid);
    const fields = dyn?.fields || [];
    return fields.includes(field);
  }, [customerList]);

  // Column visibility (localStorage persisted). Essential columns (checkbox,
  // date, route identifier, plate, actions) stay always visible; everything
  // else can be hidden per-user.
  const COLUMN_STORAGE_KEY = 'routes_visible_columns';

  // Extra data-driven columns sourced from the customer.fields definition.
  // Fields already rendered as fixed columns (date, driver, km, stops, time,
  // price…) are intentionally excluded — only truly "extra" per-customer
  // values appear here.
  const EXTRA_FIELD_COLUMNS = useMemo(() => [
    { key: 'stop_price', label: t('fields.stop_price') || 'Stop Fiyatı', type: 'currency' },
    { key: 'start_cost', label: t('fields.start_cost') || 'Başlangıç Ücreti', type: 'currency' },
    { key: 'totaal_excl_btw', label: t('fields.totaal_excl_btw') || 'Toplam (KDV hariç)', type: 'currency' },
  ], [t]);

  // Only show extra columns when at least one currently-selected customer
  // actually defines the field — prevents clutter for customers that don't
  // use it.
  const visibleExtraColumns = useMemo(
    () => EXTRA_FIELD_COLUMNS.filter(col => anySelectedHasField(col.key, [])),
    [EXTRA_FIELD_COLUMNS, anySelectedHasField]
  );

  const ALL_TOGGLE_COLUMNS = useMemo(() => [
    { key: 'customer', label: t('routes.customer') || 'Müşteri' },
    { key: 'driver', label: t('routes.driver') || 'Şoför' },
    { key: 'copilot', label: t('routes.copilot') || 'Yardımcı' },
    { key: 'km', label: t('routes.km') || 'KM' },
    { key: 'stops', label: 'Stops' },
    ...visibleExtraColumns.map(c => ({ key: c.key, label: c.label })),
    { key: 'time', label: t('routes.time') || 'Saat' },
    { key: 'price', label: t('routes.price') || 'Fiyat' },
    { key: 'total', label: t('routes.total') || 'Toplam' },
  ], [t, visibleExtraColumns]);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return ALL_TOGGLE_COLUMNS.map(c => c.key);
  });

  // Whenever the set of possible columns expands (e.g., user selects a new
  // customer with a data-driven extra field), auto-enable the newly added
  // keys so they appear the first time without a manual toggle.
  useEffect(() => {
    const allKeys = ALL_TOGGLE_COLUMNS.map(c => c.key);
    const missing = allKeys.filter(k => !visibleColumns.includes(k));
    if (missing.length > 0) {
      const next = [...visibleColumns, ...missing];
      try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next)); } catch {}
      setVisibleColumns(next);
    }
  }, [ALL_TOGGLE_COLUMNS]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const isColVisible = (key) => visibleColumns.includes(key);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Highlight/filter rows by clicking on a Route No badge — same number reveals only itself.
  const [routeNumberFilter, setRouteNumberFilter] = useState(null);

  // Apply route-number filter before pagination
  const filteredRoutes = useMemo(() => {
    if (!routeNumberFilter) return routes;
    return routes.filter((r) => String(r.route_number || '') === String(routeNumberFilter));
  }, [routes, routeNumberFilter]);

  // Calculate pagination
  const totalItems = filteredRoutes.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Get current page data
  const paginatedRoutes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredRoutes.slice(startIndex, endIndex);
  }, [filteredRoutes, currentPage, pageSize]);

  // Reset to page 1 when routes change
  useMemo(() => {
    if (currentPage > Math.ceil(filteredRoutes.length / pageSize)) {
      setCurrentPage(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRoutes.length, pageSize]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  // Color map for tour/rit values
  const tourColorMap = useCallback((value) => {
    if (!value || value === '-') return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
    const colors = [
      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300',
    ];
    let hash = 0;
    for (let i = 0; i < value.length; i++) hash = value.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }, []);

  const startEditing = (rowId, field, currentValue) => {
    setEditingCell({ rowId, field });
    setEditingValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveInlineEdit = async (route) => {
    if (!editingCell) return;
    const { field } = editingCell;
    await onUpdateInline(route, field, editingValue);
    cancelEditing();
  };

  const handleKeyDown = (e, route) => {
    if (e.key === 'Enter') {
      saveInlineEdit(route);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDriverOpen = useCallback(async (routeId, firmId) => {
    setInlineDriverOpen(routeId);
    if (firmId) {
      try {
        const employees = await onFetchEmployees(firmId);
        setInlineEmployees(employees || []);
      } catch {
        setInlineEmployees([]);
      }
    }
  }, [onFetchEmployees]);

  const handleDriverSelect = async (route, employee) => {
    await onUpdateDriver(route, employee);
    setInlineDriverOpen(null);
  };

  const handleCopilotOpen = useCallback(async (routeId, firmId) => {
    setInlineCopilotOpen(routeId);
    if (firmId) {
      try {
        const employees = await onFetchEmployees(firmId);
        setInlineEmployees(employees || []);
      } catch {
        setInlineEmployees([]);
      }
    }
  }, [onFetchEmployees]);

  const handleCopilotSelect = async (route, employee) => {
    const copilotName = employee.user?.full_name || `${employee.profile?.voornaam || ''} ${employee.profile?.naam || ''}`.trim();
    await onUpdateInline(route, 'copilot_name', copilotName);
    await onUpdateInline(route, 'copilot_id', employee.user?.id || '');
    setInlineCopilotOpen(null);
  };

  const [exportOpen, setExportOpen] = useState(false);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (routes.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-slate-400">
            <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('routes.noRecords') || 'Bu ay için kayıt bulunamadı'}</p>
            <Button variant="outline" className="mt-4" onClick={onAdd}>
              <Plus className="h-4 w-4 mr-2" />
              {t('routes.addFirstRecord') || 'İlk Kaydı Ekle'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {Array.from(selectedCustomers).map(cid => (
              <Badge key={cid} className={getCustomerColor(cid)}>
                {getCustomerName(cid)}
              </Badge>
            ))}
            <span className="truncate">{t('routes.allRoutes')}</span>
            <Badge variant="outline" className="font-normal shrink-0">
              {totalItems} {t('routes.records')}
            </Badge>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="route-columns-btn" title="Sütunları Göster/Gizle">
                  <Columns3 className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Sütunlar</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <p className="text-xs font-semibold text-slate-500 mb-2">Görünür sütunlar</p>
                <div className="space-y-1">
                  {ALL_TOGGLE_COLUMNS.map(col => (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                      data-testid={`route-col-toggle-${col.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-slate-300"
                      />
                      <span className="text-slate-700 dark:text-slate-300">{col.label}</span>
                    </label>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      onClick={() => {
                        const all = ALL_TOGGLE_COLUMNS.map(c => c.key);
                        setVisibleColumns(all);
                        try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(all)); } catch {}
                      }}
                      data-testid="route-reset-cols-btn"
                    >
                      Tümünü göster
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button 
              size="sm"
              onClick={onAdd}
              data-testid="table-add-route-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Plus className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{t('routes.newRoute') || 'Yeni Route'}</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setExportOpen(true)}
              disabled={filteredRoutes.length === 0}
              data-testid="filtered-export-btn"
              title={t('routes.exportFiltered') || 'Filtrelenmiş veriyi dışa aktar'}
            >
              <Download className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{t('routes.exportExcel') || 'Excel İndir'}</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{totalItems}</Badge>
            </Button>
            <ExportColumnsDialog
              open={exportOpen}
              onOpenChange={setExportOpen}
              routes={filteredRoutes}
              firms={firms}
              isMultiCustomer={isMultiCustomer}
              getCustomerName={getCustomerName}
            />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Pencil className="h-3 w-3" />
            {t('routes.table.clickToEdit')} ({t('routes.table.hoverIcon')})
          </p>
          <div className="flex items-center gap-2">
            {routeNumberFilter && (
              <Badge
                variant="outline"
                className="h-7 gap-1.5 pl-2 pr-1 text-xs border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700/60"
                data-testid="route-number-filter-chip"
              >
                Route No: <span className="font-semibold">{routeNumberFilter}</span>
                <button
                  type="button"
                  onClick={() => setRouteNumberFilter(null)}
                  className="ml-0.5 rounded-full hover:bg-blue-200/60 dark:hover:bg-blue-800/60 w-4 h-4 inline-flex items-center justify-center"
                  aria-label="Clear route number filter"
                  data-testid="route-number-filter-clear"
                >
                  ×
                </button>
              </Badge>
            )}
            {selectedRows.size > 0 && (
              <>
                <Button 
                  variant="default"
                  size="sm" 
                  onClick={onBulkClone}
                  disabled={bulkCloning}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="bulk-clone-btn"
                  title={t('routes.copyWithToday')}
                >
                  {bulkCloning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {selectedRows.size} {t('routes.copyRecords') || 'kayıt kopyala'}
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={onBulkDelete}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {selectedRows.size} {t('routes.deleteRecords')}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={paginatedRoutes.length > 0 && paginatedRoutes.every(r => selectedRows.has(r.id))}
                    onCheckedChange={() => onToggleAllRows(paginatedRoutes)}
                    aria-label={t('routes.selectAllOnPage')}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                {isMultiCustomer && isColVisible('customer') && <TableHead>{t('routes.customer')}</TableHead>}
                <TableHead>{t('routes.date')}</TableHead>
                <TableHead>
                  {isMultiCustomer
                    ? (t('routes.routeRitTour') || 'Route/Rit/Tur')
                    : getRouteIdentifier(Array.from(selectedCustomers)[0]).label}
                </TableHead>
                <TableHead>{t('routes.licensePlate')}</TableHead>
                {isColVisible('driver') && <TableHead>{t('routes.driver')}</TableHead>}
                {isColVisible('copilot') && anySelectedHasField('copilot', ['mover']) && <TableHead>{t('routes.copilot')}</TableHead>}
                {isColVisible('km') && (anySelectedHasField('km_start', ['fedex','dhl','bpost']) || anySelectedHasField('km_end', [])) && <TableHead>{t('routes.km')}</TableHead>}
                {isColVisible('stops') && anySelectedHasField('stops', []) && <TableHead>Stops</TableHead>}
                {visibleExtraColumns.map(col => (
                  isColVisible(col.key) && (
                    <TableHead key={col.key}>{col.label}</TableHead>
                  )
                ))}
                {isColVisible('time') && <TableHead>{t('routes.time')}</TableHead>}
                {isColVisible('price') && <TableHead>{t('routes.price')}</TableHead>}
                {isColVisible('total') && <TableHead className="text-right">{t('routes.total')}</TableHead>}
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRoutes.map((route) => (
                <TableRow 
                  key={route.id} 
                  className={`group transition-colors ${
                    recentlyClonedIds?.has?.(route.id)
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 border-l-4 border-l-emerald-500 shadow-sm animate-pulse-slow'
                      : selectedRows.has(route.id) 
                        ? 'bg-blue-100 dark:bg-blue-900/40 border-l-4 border-l-blue-600 shadow-sm' 
                        : ''
                  }`}
                  data-testid={`route-row-${route.id}`}
                >
                  {/* Checkbox */}
                  <TableCell className="w-12">
                    <Checkbox
                      checked={selectedRows.has(route.id)}
                      onCheckedChange={() => onToggleRow(route.id)}
                      aria-label={t('routes.selectRow')}
                      data-testid={`row-checkbox-${route.id}`}
                    />
                  </TableCell>

                  {/* Müşteri Badge */}
                  {isMultiCustomer && isColVisible('customer') && (
                    <TableCell>
                      <Badge className={getCustomerColor(route.customer)}>
                        {getCustomerName(route.customer)}
                      </Badge>
                    </TableCell>
                  )}

                  {/* Tarih */}
                  <TableCell className="font-medium">
                    {editingCell?.rowId === route.id && editingCell?.field === 'date' ? (
                      <Input
                        type="date"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => saveInlineEdit(route)}
                        onKeyDown={(e) => handleKeyDown(e, route)}
                        autoFocus
                        className="w-32 h-8 text-sm"
                      />
                    ) : (
                      <EditableCell onDoubleClick={() => startEditing(route.id, 'date', route.date?.split('T')[0])}>
                        {route.date && !isNaN(new Date(route.date).getTime()) 
                          ? new Date(route.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
                          : route.date || '-'}
                      </EditableCell>
                    )}
                  </TableCell>
                  
                  {/* Route No / Rit / Tur */}
                  <TableCell>
                    {(() => {
                      const ident = getRouteIdentifier(route.customer);
                      if (ident.key === 'route_number') {
                        // Brand-fixed customers keep their corporate colors; others get
                        // deterministic per-route-number coloring (same number → same color).
                        const brandClass =
                          route.customer === 'bpost'
                            ? 'border-red-300 text-red-700 dark:border-red-700/60 dark:text-red-300'
                            : route.customer === 'dhl'
                            ? 'border-yellow-300 text-yellow-700 dark:border-yellow-700/60 dark:text-yellow-300'
                            : null;
                        const num = route.route_number;
                        const isActive = routeNumberFilter && String(routeNumberFilter) === String(num);
                        return (
                          <Badge
                            variant={brandClass ? 'outline' : undefined}
                            className={`${brandClass || tourColorMap(num)} ${num ? 'cursor-pointer hover:opacity-80' : ''} ${isActive ? 'ring-2 ring-offset-1 ring-blue-400 dark:ring-blue-500 dark:ring-offset-slate-900' : ''}`}
                            onClick={() => {
                              if (!num) return;
                              setRouteNumberFilter((prev) => (String(prev) === String(num) ? null : num));
                            }}
                            data-testid={`route-no-badge-${route.id}`}
                            title={num ? (isActive ? 'Filtreyi kaldır' : `"${num}" numaralı turları göster`) : undefined}
                          >
                            {num || '-'}
                          </Badge>
                        );
                      }
                      if (ident.key === 'plaats') {
                        return editingCell?.rowId === route.id && editingCell?.field === 'plaats' ? (
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => saveInlineEdit(route)}
                            onKeyDown={(e) => handleKeyDown(e, route)}
                            autoFocus
                            className="w-24 h-8 text-sm"
                          />
                        ) : (
                          <Badge
                            className={`cursor-pointer hover:opacity-80 ${tourColorMap(route.plaats)}`}
                            onDoubleClick={() => startEditing(route.id, 'plaats', route.plaats)}
                          >
                            {route.plaats || '-'}
                          </Badge>
                        );
                      }
                      return <Badge className={tourColorMap(route.route_type)}>{route.route_type || '-'}</Badge>;
                    })()}
                  </TableCell>
                  
                  {/* Plaka */}
                  <TableCell>
                    {editingCell?.rowId === route.id && editingCell?.field === 'license_plate' ? (
                      <Input
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value.toUpperCase())}
                        onBlur={() => saveInlineEdit(route)}
                        onKeyDown={(e) => handleKeyDown(e, route)}
                        autoFocus
                        className="w-24 h-8 text-sm uppercase"
                      />
                    ) : (
                      <EditableCell onDoubleClick={() => startEditing(route.id, 'license_plate', route.license_plate)}>
                        {route.license_plate}
                      </EditableCell>
                    )}
                  </TableCell>
                  
                  {/* Şoför */}
                  {isColVisible('driver') && (
                  <TableCell className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                    <Popover 
                      open={inlineDriverOpen === route.id} 
                      onOpenChange={(open) => {
                        if (open) {
                          handleDriverOpen(route.id, route.firm_id);
                        } else {
                          setInlineDriverOpen(null);
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <div className="flex items-center gap-1 group-hover:underline decoration-dotted">
                          <span>{route.driver_name}</span>
                          <User className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('routes.form.searchDriver')} />
                          <CommandList>
                            <CommandEmpty>
                              {inlineEmployees.length === 0 ? t('routes.form.loading') : t('routes.form.driverNotFound')}
                            </CommandEmpty>
                            <CommandGroup>
                              {inlineEmployees.map(emp => {
                                const driverName = emp.user?.full_name || `${emp.profile?.voornaam || ''} ${emp.profile?.naam || ''}`.trim();
                                return (
                                  <CommandItem
                                    key={emp.user?.id}
                                    value={driverName}
                                    onSelect={() => handleDriverSelect(route, emp)}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${route.driver_id === emp.user?.id ? "opacity-100" : "opacity-0"}`}
                                    />
                                    {driverName}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  )}

                  {/* Copilot/Yardımcı - Şoför gibi seçilebilir */}
                  {isColVisible('copilot') && anySelectedHasField('copilot', ['mover']) && (
                    <TableCell className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                      {route.customer === 'mover' ? (
                        <Popover 
                          open={inlineCopilotOpen === route.id} 
                          onOpenChange={(open) => {
                            if (open) {
                              handleCopilotOpen(route.id, route.firm_id);
                            } else {
                              setInlineCopilotOpen(null);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <div className="flex items-center gap-1 group-hover:underline decoration-dotted text-slate-600 dark:text-slate-300">
                              <span>{route.copilot_name || '-'}</span>
                              <User className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder={t('routes.form.searchCopilot')} />
                              <CommandList>
                                <CommandEmpty>
                                  {inlineEmployees.length === 0 ? t('routes.form.loading') : t('routes.form.noEmployeeFound')}
                                </CommandEmpty>
                                <CommandGroup>
                                  {inlineEmployees.map(emp => {
                                    const copilotName = emp.user?.full_name || `${emp.profile?.voornaam || ''} ${emp.profile?.naam || ''}`.trim();
                                    return (
                                      <CommandItem
                                        key={emp.user?.id}
                                        value={copilotName}
                                        onSelect={() => handleCopilotSelect(route, emp)}
                                      >
                                        <Check
                                          className={`mr-2 h-4 w-4 ${route.copilot_id === emp.user?.id ? "opacity-100" : "opacity-0"}`}
                                        />
                                        {copilotName}
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                  )}
                  
                  {/* KM - Sadece gösterim, düzenlenemez */}
                  {isColVisible('km') && (anySelectedHasField('km_start', ['fedex','dhl','bpost']) || anySelectedHasField('km_end', [])) && (
                    <TableCell>
                      <span className="text-slate-600 dark:text-slate-300">
                        {route.km_total || '-'}
                      </span>
                    </TableCell>
                  )}

                  {/* Stops - any customer that defines the `stops` field */}
                  {isColVisible('stops') && anySelectedHasField('stops', []) && (
                    <TableCell>
                      {routeHasField(route, 'stops') ? (
                        <span className="font-medium text-slate-700 dark:text-slate-200">{route.stops || 0}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                  )}

                  {/* Dynamic extra columns from customer.fields (stop_price,
                      start_cost, totaal_excl_btw) — shown per-row only when
                      that route's customer actually defines the field. */}
                  {visibleExtraColumns.map(col => (
                    isColVisible(col.key) && (
                      <TableCell key={col.key}>
                        {routeHasField(route, col.key) ? (
                          col.type === 'currency' ? (
                            <span className="text-slate-700 dark:text-slate-200">
                              <Money amount={Number(route[col.key]) || 0} revealKey={`route-${route.id}-${col.key}`} />
                            </span>
                          ) : (
                            <span className="text-slate-700 dark:text-slate-200">{route[col.key] ?? '-'}</span>
                          )
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                    )
                  ))}
                  
                  {/* Saat */}
                  {isColVisible('time') && (
                  <TableCell className="min-w-[130px]">
                    <div className="flex flex-col gap-1">
                      {editingCell?.rowId === route.id && editingCell?.field === 'start_time' ? (
                        <Input
                          type="time"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveInlineEdit(route)}
                          onKeyDown={(e) => handleKeyDown(e, route)}
                          autoFocus
                          className="w-24 h-7 text-xs"
                        />
                      ) : (
                        <div 
                          className="group/time flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 px-1 rounded"
                          onDoubleClick={() => startEditing(route.id, 'start_time', route.start_time)}
                          title={t('routes.table.doubleClickStart')}
                        >
                          <span>🕐 {route.start_time}</span>
                          <Pencil className="h-2.5 w-2.5 text-slate-400 opacity-0 group-hover/time:opacity-100 transition-opacity" />
                        </div>
                      )}
                      {editingCell?.rowId === route.id && editingCell?.field === 'end_time' ? (
                        <Input
                          type="time"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveInlineEdit(route)}
                          onKeyDown={(e) => handleKeyDown(e, route)}
                          autoFocus
                          className="w-24 h-7 text-xs"
                        />
                      ) : (
                        <div 
                          className="group/time flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 px-1 rounded"
                          onDoubleClick={() => startEditing(route.id, 'end_time', route.end_time)}
                          title={t('routes.table.doubleClickEnd')}
                        >
                          <span>🕕 {route.end_time}</span>
                          <Pencil className="h-2.5 w-2.5 text-slate-400 opacity-0 group-hover/time:opacity-100 transition-opacity" />
                        </div>
                      )}
                      <span className="font-medium text-emerald-600">{formatHours(route.work_hours)}</span>
                    </div>
                  </TableCell>
                  )}
                  
                  {/* Ücret */}
                  {isColVisible('price') && (
                  <TableCell>
                    {(() => {
                      const calcType = getCalcType(route.customer);
                      if (calcType === 'manual_total') {
                        return (
                          <EditableCell onDoubleClick={() => startEditing(route.id, 'start_cost', route.start_cost)}>
                            <Money amount={route.start_cost || 0} revealKey={`route-${route.id}-start_cost`} />
                          </EditableCell>
                        );
                      }
                      if (calcType === 'stops_price') {
                        return (
                          <EditableCell onDoubleClick={() => startEditing(route.id, 'start_cost', route.start_cost)}>
                            <span className="text-slate-600 dark:text-slate-300"><Money amount={route.start_cost || 0} suffix="/stop" revealKey={`route-${route.id}-stops_price`} /></span>
                          </EditableCell>
                        );
                      }
                      // hours_rate
                      if (editingCell?.rowId === route.id && editingCell?.field === 'hourly_rate') {
                        return (
                          <Input
                            type="number"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => saveInlineEdit(route)}
                            onKeyDown={(e) => handleKeyDown(e, route)}
                            autoFocus
                            className="w-16 h-8 text-sm"
                          />
                        );
                      }
                      return (
                        <EditableCell onDoubleClick={() => startEditing(route.id, 'hourly_rate', route.hourly_rate)}>
                          <Money amount={route.hourly_rate} suffix="/s" revealKey={`route-${route.id}-hourly_rate`} />
                        </EditableCell>
                      );
                    })()}
                  </TableCell>
                  )}
                  
                  {/* Toplam */}
                  {isColVisible('total') && (
                  <TableCell className="text-right font-bold text-emerald-600">
                    <Money amount={route.total_cost} revealKey={`route-${route.id}-total_cost`} />
                  </TableCell>
                  )}
                  
                  {/* Actions */}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => onEdit(route)} title={t('routes.editAllFields')}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => onViewAttachments && onViewAttachments(route)} 
                        title={t('routes.viewAttachments') || 'Dosya Ekleri'}
                        className="relative"
                      >
                        <Paperclip className="h-4 w-4 text-slate-500" />
                        {route.attachment_count > 0 && (
                          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                            {route.attachment_count}
                          </span>
                        )}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => onClone(route)} title={t('routes.copyWithToday')}>
                        <Copy className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => onDelete(route.id)} title={t('routes.delete')}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {totalItems > 10 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t">
            {/* Left: Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">{t('routes.perPage')}:</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} / {totalItems}
              </span>
            </div>

            {/* Right: Navigation buttons */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                title={t('routes.firstPage')}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                title={t('routes.previousPage')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {/* Page numbers - hide on very small screens */}
              <div className="hidden xs:flex items-center gap-1 mx-1">
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage <= 2) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 1) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = currentPage - 1 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              {/* Compact page info for mobile */}
              <span className="xs:hidden text-sm text-slate-600 dark:text-slate-400 mx-2">{currentPage}/{totalPages}</span>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                title={t('routes.nextPage')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                title={t('routes.lastPage')}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
